/**
 * Geçmiş cevaplar state (v0.1.38): the list (GET /reply-examples), loaded when the screen
 * opens; retire and restore go to the server and the list is read again, so the page shows
 * what the server holds and never a guessed state.
 */

import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import type { ReplyExamplesView } from '../api/reply-examples.js';
import { useStore } from './context.js';

export interface ReplyExamplesState {
  view: ReplyExamplesView | null;
  error: string | null;
  loading: boolean;
  /** Example ids with a decision in flight. */
  busy: readonly string[];
}

export class ReplyExamplesStore {
  private state: ReplyExamplesState = { view: null, error: null, loading: false, busy: [] };
  private readonly listeners = new Set<() => void>();
  private inFlight: Promise<void> | null = null;

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): ReplyExamplesState => this.state;

  private set(patch: Partial<ReplyExamplesState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  ensureLoaded(): Promise<void> {
    if (this.state.view !== null || this.inFlight !== null) return this.inFlight ?? Promise.resolve();
    return this.reload();
  }

  reload(): Promise<void> {
    this.set({ loading: true });
    const run = (async () => {
      try {
        const view = await this.api.getReplyExamples();
        this.set({ view, error: null, loading: false });
      } catch (error) {
        this.set({ error: error instanceof Error ? error.message : String(error), loading: false });
      }
    })();
    this.inFlight = run;
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null;
    });
    return run;
  }

  /** Takes an example out of the Temsilci's set; resolves to the error text when refused. */
  retire(id: string): Promise<string | null> {
    return this.decide(id, () => this.api.retireReplyExample(id));
  }

  restore(id: string): Promise<string | null> {
    return this.decide(id, () => this.api.restoreReplyExample(id));
  }

  private async decide(id: string, call: () => Promise<void>): Promise<string | null> {
    if (this.state.busy.includes(id)) return null;
    this.set({ busy: [...this.state.busy, id] });
    try {
      await call();
      await this.reload();
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : String(error);
    } finally {
      this.set({ busy: this.state.busy.filter((item) => item !== id) });
    }
  }
}

export function useReplyExamples(): { store: ReplyExamplesStore; state: ReplyExamplesState } {
  const store = useStore().replyExamples;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
